import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

interface NetworkInformation {
  effectiveType?: string;
}

const TRANSIENT_CODES = new Set([
  'unavailable',
  'deadline-exceeded',
  'resource-exhausted',
]);

const IGNORED_MESSAGES = [
  'network-request-failed',
  'Failed to fetch',
  'Load failed',
  'NetworkError',
];

const NON_PRODUCTION_PREFIXES = ['/demo'];

function isNonProductionRoute(): boolean {
  return NON_PRODUCTION_PREFIXES.some((p) => window.location.pathname.startsWith(p));
}

let posthogReady = false;

export function initMonitoring(): void {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  if (sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment: import.meta.env.DEV ? 'development' : 'production',
      beforeSend(event) {
        const msg = event.exception?.values?.[0]?.value || '';
        if (IGNORED_MESSAGES.some((m) => msg.includes(m))) return null;
        if (isNonProductionRoute()) {
          event.tags = { ...event.tags, source: 'demo' };
        }
        return event;
      },
    });
  }

  const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
  if (posthogKey) {
    posthog.init(posthogKey, {
      api_host: 'https://us.i.posthog.com',
      autocapture: false,
      capture_pageview: true,
      persistence: 'localStorage+cookie',
      disable_session_recording: false,
      session_recording: {
        recordCrossOriginIframes: true,
        maskAllInputs: true,
        maskTextSelector: '[data-ph-mask]',
      },
      before_send(event) {
        if (isNonProductionRoute()) return null;
        return event;
      },
      loaded(ph) {
        if (import.meta.env.DEV) ph.opt_out_capturing();
        posthogReady = true;
      },
    });
    posthogReady = true;

    try {
      const nav = navigator as Navigator & { connection?: NetworkInformation };
      const ct = nav.connection?.effectiveType;
      if (ct) posthog.register({ connection_type: ct });
    } catch (_) { /* navigator.connection unsupported */ }
  }
}

export function identifyUser(uid: string, properties: Record<string, string | null> = {}): void {
  Sentry.setUser({ id: uid, ...properties });
  if (posthogReady) {
    posthog.identify(uid, {
      ...properties,
      $name: properties.phone || properties.name || uid,
      $phone: properties.phone || null,
    });
  }
}

export function resetUser(): void {
  Sentry.setUser(null);
  if (posthogReady) posthog.reset();
}

export function trackEvent(name: string, properties: Record<string, unknown> = {}): void {
  if (posthogReady && !isNonProductionRoute()) posthog.capture(name, properties);
}

export function captureError(error: unknown, context: Record<string, unknown> = {}): void {
  Sentry.withScope((scope) => {
    for (const [key, val] of Object.entries(context)) {
      scope.setExtra(key, val);
    }
    Sentry.captureException(error);
  });
}

export function isTransientFirestoreError(err: { code?: string } | null | undefined): boolean {
  return TRANSIENT_CODES.has(err?.code ?? '');
}
